import { Canvas } from '@react-three/fiber'
import { createFileRoute } from '@tanstack/react-router'
import { LevaPanel, useControls } from 'leva'
import { Suspense, useEffect, useMemo, useState } from 'react'
import AsciiColorGradient from '../components/three/scene'
import type { } from '../routeTree.gen'

export const Route = createFileRoute('/canvas/custom' as any)({
  component: RouteComponent,
})

function RouteComponent() {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    console.log('[canvas/custom] mount effect')
    setMounted(true)
    return () => {
      console.log('[canvas/custom] unmount')
    }
  }, [])

  // Defaults from provided payload, filtered to supported props
  const defaults = useMemo(
    () => ({
      animSpeed: 0.33,
      noiseSpeed: 0,
      rotationAmount: 172,
      waveFreq: 3.7,
      waveAmp: 43,
      waveSpeed: 0.037,
      waveFreqY: 7.5,
      waveAmpY: 60,
      color1: { r: 210, g: 47, b: 11 },
      color2: { r: 62, g: 132, b: 229 },
      color3: { r: 100.7, g: 185.1, b: 241.1 },
      color4: { r: 184.7, g: 207.8, b: 219.4 },
      gradientAngle: 28,
      gradientSmooth1: 0.5,
      gradientSmooth2: 0.5,
      gradientPos1: -0.2,
      gradientPos2: -0.4,
      gradientMixPos: 0.6,
      darkPulseSpeed: 0.08,
      darkPulseAmount: 0.3,
      darkScale1: 0.15,
      darkSpeed1: 0.015,
      darkIntensity1: 1.1,
      darkThreshold1: -0.8,
      darkPower1: 3,
      darkScale2: 0.1,
      darkSpeed2: 0.012,
      darkIntensity2: 0.4,
      darkThreshold2: -0.4,
      darkPower2: 0.8,
      darkScale3: 0.12,
      darkSpeed3: 0.02,
      darkIntensity3: 1.2,
      darkThreshold3: -0.7,
      darkPower3: 2.5,
      saturation: 0.59,
      temperature: -0.2,
      hue: 31,
      gamma: 1,
      brightness: 0,
      contrast: 1.3,
      blackLevel: 0.05,
      whiteLevel: 0.85,
      grainAmount: 0,
      grainSize: 0,
      cellSize: 13,
      asciiInvert: false,
      asciiGamma: 1,
      asciiContrast: 1.15,
      asciiBrightness: -0.22,
      asciiRangeMin: 0.18,
      asciiRangeMax: 0.4,
      asciiActiveCount: 62,
      asciiShift: 33,
      asciiGlyphSeed: 0,
      asciiPrimaryColor: { r: 255, g: 255, b: 255 },
      asciiSecondaryColor: { r: 35, g: 31, b: 32 },
      asciiSwapColors: false,
      asciiEnabled: true,
    }),
    []
  );

  const controls = useControls('Properties', {
    cellSize: { value: defaults.cellSize, min: 4, max: 32, step: 1 },
    asciiGamma: { value: defaults.asciiGamma, min: 0.1, max: 3, step: 0.01 },
    asciiContrast: { value: defaults.asciiContrast, min: 0, max: 3, step: 0.01 },
    asciiBrightness: { value: defaults.asciiBrightness, min: -1, max: 1, step: 0.01 },
    asciiRangeMin: { value: defaults.asciiRangeMin, min: 0, max: 1, step: 0.01 },
    asciiRangeMax: { value: defaults.asciiRangeMax, min: 0, max: 1, step: 0.01 },
    asciiActiveCount: { value: defaults.asciiActiveCount, min: 1, max: 100, step: 1 },
    asciiShift: { value: defaults.asciiShift, min: 0, max: 100, step: 1 },
    asciiSwapColors: { value: defaults.asciiSwapColors },
  })

  const cssToRgb = (css: string) => {
    const m = css.match(/rgba?\\((\\d+),\\s*(\\d+),\\s*(\\d+)\\)/i)
    if (!m) return { r: 255, g: 255, b: 255 }
    return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) }
  }

  return (
    <>
      {mounted ? (
        <div className="fixed top-4 right-4 z-[9999] pointer-events-auto">  
          <LevaPanel
            collapsed={false}
            oneLineLabels
            fill={false}
            flat={false}
            hidden={false}
            neverHide
            titleBar={{ drag: true, filter: true }}
          />
        </div>
      ) : null}
      <div className="fixed inset-0 -z-10">
      <Canvas dpr={[1, 1.5]} gl={{ antialias: true }}>
        <Suspense fallback={null}>
          <AsciiColorGradient
            // Gradient core
            animSpeed={defaults.animSpeed}
            noiseSpeed={defaults.noiseSpeed}
            rotationAmount={defaults.rotationAmount}
            waveFreq={defaults.waveFreq}
            waveAmp={defaults.waveAmp}
            waveSpeed={defaults.waveSpeed}
            waveFreqY={defaults.waveFreqY}
            waveAmpY={defaults.waveAmpY}
            // Colors
            color1={
              { r: 255, g: 255, b: 255 }
            }
            color2={
              { r: 255, g: 255, b: 255 }
            }
            color3={
              { r: 255, g: 255, b: 255 }
            }
            color4={
              { r: 255, g: 255, b: 255 }
            }
            // Gradient mixing
            gradientAngle={defaults.gradientAngle}
            gradientSmooth1={defaults.gradientSmooth1}
            gradientSmooth2={defaults.gradientSmooth2}
            gradientPos1={defaults.gradientPos1}
            gradientPos2={defaults.gradientPos2}
            gradientMixPos={defaults.gradientMixPos}
            // Dark layering
            darkPulseSpeed={defaults.darkPulseSpeed}
            darkPulseAmount={defaults.darkPulseAmount}
            darkScale1={defaults.darkScale1}
            darkSpeed1={defaults.darkSpeed1}
            darkIntensity1={defaults.darkIntensity1}
            darkThreshold1={defaults.darkThreshold1}
            darkPower1={defaults.darkPower1}
            darkScale2={defaults.darkScale2}
            darkSpeed2={defaults.darkSpeed2}
            darkIntensity2={defaults.darkIntensity2}
            darkThreshold2={defaults.darkThreshold2}
            darkPower2={defaults.darkPower2}
            darkScale3={defaults.darkScale3}
            darkSpeed3={defaults.darkSpeed3}
            darkIntensity3={defaults.darkIntensity3}
            darkThreshold3={defaults.darkThreshold3}
            darkPower3={defaults.darkPower3}
            // Post
            saturation={defaults.saturation}
            temperature={defaults.temperature}
            hue={defaults.hue}
            gamma={defaults.gamma}
            brightness={defaults.brightness}
            contrast={defaults.contrast}
            blackLevel={defaults.blackLevel}
            whiteLevel={defaults.whiteLevel}
            grainAmount={defaults.grainAmount}
            grainSize={defaults.grainSize}
            // ASCII
            cellSize={controls.cellSize}
            asciiEnabled={defaults.asciiEnabled}
            asciiInvert={defaults.asciiInvert}
            asciiGamma={controls.asciiGamma}
            asciiContrast={controls.asciiContrast}
            asciiBrightness={controls.asciiBrightness}
            asciiRangeMin={controls.asciiRangeMin}
            asciiRangeMax={controls.asciiRangeMax}
            asciiActiveCount={controls.asciiActiveCount}
            asciiShift={controls.asciiShift}
            asciiPrimaryColor={defaults.asciiPrimaryColor}
            asciiSecondaryColor={defaults.asciiSecondaryColor}
            asciiSwapColors={defaults.asciiSwapColors}
          />
        </Suspense>
      </Canvas>
      </div>
    </>
  )
}


